// Outgoing buffer for the intercom signalling WebSocket.
//
// WebRTC offers, answers and ICE candidates can be published before the
// signalling socket reaches OPEN. Those messages used to be dropped silently
// and the call stalled with no trace (#3). The queue keeps them in insertion
// order until the socket's `onopen` handler drains it.
export const DEFAULT_SIGNAL_QUEUE_MAX = 50

// Numeric readyState values from the WebSocket spec. Kept local so the pure
// module (and its tests) do not depend on a global WebSocket implementation.
export const SIGNAL_SOCKET_CONNECTING = 0
export const SIGNAL_SOCKET_OPEN = 1

export type SignalQueueOverflowHandler<T> = (dropped: T, max: number) => void

export type SignalQueue<T> = {
  enqueue: (item: T) => void
  drain: () => T[]
  clear: () => void
  readonly size: number
}

export const createSignalQueue = <T>({
  max = DEFAULT_SIGNAL_QUEUE_MAX,
  onOverflow,
}: {
  max?: number
  onOverflow?: SignalQueueOverflowHandler<T>
} = {}): SignalQueue<T> => {
  if (!Number.isInteger(max) || max < 1) {
    throw new RangeError(`signalQueue: max must be a positive integer, got ${max}`)
  }

  const items: T[] = []

  const reportOverflow = (dropped: T) => {
    console.warn(
      `[SIGNALING] outgoing signal queue full (max ${max}); dropping oldest queued signal`,
      dropped
    )
    onOverflow?.(dropped, max)
  }

  return {
    enqueue(item) {
      if (items.length >= max) {
        reportOverflow(items.shift() as T)
      }
      items.push(item)
    },
    drain() {
      return items.splice(0, items.length)
    },
    clear() {
      items.length = 0
    },
    get size() {
      return items.length
    },
  }
}

export type SignalSocket<T> = {
  readyState: number
  send: (message: T) => void
}

export type DeliverSignalResult = 'queued' | 'sent' | 'dropped'

// Route one already-serialized signal through the socket or the pending queue.
// Extracted from useCall's sendViaSignaling so the bug seam (#3) is measurable
// without a real WebSocket: queued while CONNECTING, sent when OPEN, dropped
// when the call is closed or the socket is CLOSING/CLOSED.
export const deliverSignal = <T>({
  ws,
  message,
  queue,
  callClosed,
  type,
}: {
  ws?: SignalSocket<T> | null
  message: T
  queue: SignalQueue<T>
  callClosed: boolean
  type?: string
}): DeliverSignalResult => {
  if (callClosed) {
    console.log('[SIGNALING] dropping outgoing signal; call is closed', type)
    return 'dropped'
  }
  if (!ws || ws.readyState === SIGNAL_SOCKET_CONNECTING) {
    queue.enqueue(message)
    console.log('[SIGNALING] queued outgoing signal until websocket opens', {
      type,
      queued: queue.size,
    })
    return 'queued'
  }
  if (ws.readyState === SIGNAL_SOCKET_OPEN) {
    ws.send(message)
    return 'sent'
  }
  // CLOSING/CLOSED: no reconnect exists yet (separate task). Drop loudly
  // instead of silently losing the signal.
  console.warn('[SIGNALING] dropping outgoing signal; websocket is not open', {
    type,
    readyState: ws.readyState,
  })
  return 'dropped'
}
